<script setup lang="ts">
import { buildListingPostalAddress } from "~/utils/schema-org-mappings";
import { pseoTypeFromHomeType } from "~/utils/pseo-guest-query";
import type { FeaturedThisWeekResponse } from "~~/server/api/establishments/featured-this-week.get";

/**
 * HeroHomeV3 — Hero éditorial pour la page d'accueil (refonte V3 Editorial sage).
 *
 * UN SEUL DOM responsive (story « Hero en un seul DOM ») — plus de copie par
 * viewport : un sélecteur, une pill, un « À la une », un bandeau de confiance.
 * Grille à zones implicites, l'ordre < lg suit le flux source :
 *
 * - < lg (mobile + tablette) : une colonne — A (eyebrow, accroche, sélecteur +
 *   pill) → B (« À la une ») → C (bandeau de confiance).
 * - ≥ lg : colonne gauche A puis C, colonne droite B placé explicitement sur
 *   les deux rangées (`grid-rows-[auto_1fr]` : B n'étire pas la rangée de A).
 *
 * Zone A : parcours en étapes `HoliHomeSearchWizard` quand le réglage
 * `runtimeConfig.public.homeWizard` est vrai (défaut), suivi des 2 claims fixes
 * du bandeau de confiance (`HoliTrustStripV3 variant="fixed"`) sur toute la
 * largeur de la zone A — la zone C n'est alors pas rendue (le 3e claim
 * doublonnait le texte des cartes de type). Réglage faux : sélecteur de type +
 * pill (state de recherche de useHomeSearch() via HoliHeroSearchPill) et
 * bandeau 3 claims en zone C, inchangés.
 */
import type { HomeSearchWizardRegion } from "~/components/holi/HomeSearchWizard.vue";

withDefaults(
  defineProps<{
    /** Régions triées par densité du type actif (`index.vue`), pour le parcours. */
    regions?: HomeSearchWizardRegion[];
  }>(),
  { regions: () => [] },
);

const { t } = useI18n();
const config = useRuntimeConfig();
// Type actif (switcher) — pilote l'eyebrow chiffré et la teinte du hero.
// `accentInkVar` (et non `accentVar`) : le soulignement est un TRAIT sur fond
// paper clair — l'ocre de marque n'y tient pas le contraste, sa variante encre si.
const { type, accentInkVar } = useHomeType();

// Fetch featured-this-week (lazy — le hero V3 se rend immédiatement avec placeholders)
const { data: featuredData, status: featuredStatus } = useLazyAsyncData(
  "featured-this-week",
  () =>
    rawFetch<FeaturedThisWeekResponse>("/api/establishments/featured-this-week"),
  {
    default: () =>
      ({
        data: { featured: [], recentByDepartment: [] },
      }) as FeaturedThisWeekResponse,
  },
);

const featured = computed(() => featuredData.value?.data.featured ?? []);

// Eyebrow chiffré piloté par le type actif : compte EXACT de `/api/search/count`,
// même filtrage que la page de résultats (le chiffre promis est celui affiché).
// Clé réactive sur le type : basculer le switcher relance le comptage. Attendu
// en SSR même en lazy (contexte composant) → pas de flash au premier rendu.
const { data: searchCountData } = useLazyAsyncData(
  () => `home-search-count-${type.value}`,
  () =>
    rawFetch<{ total: number; expandedRadiusKm?: number }>("/api/search/count", {
      query: { type: pseoTypeFromHomeType(type.value) },
    }),
);
const typedCount = computed(() => searchCountData.value?.total ?? 0);

// Erreur (503…) ou 0 → libellé de repli sans chiffre.
const eyebrowLabel = computed(() =>
  typedCount.value > 0
    ? t(`pages.home.homeType.eyebrow.${type.value}`, { count: typedCount.value }, typedCount.value)
    : t("pages.home.heroV3.eyebrowFallback"),
);

// Loading state pour skeleton bloc éditorial
const isFeaturedLoading = computed(() => featuredStatus.value === "pending");

// Sens d'ouverture des suggestions de la pill : vers le haut < md (le clavier
// virtuel masquerait une liste ancrée sous le champ, même motif que /search).
// SSR rend `down` (la liste ne s'ouvre qu'en client) ; corrigé au montage.
const pillDrop = ref<"down" | "up">("down");
let mobileQuery: MediaQueryList | null = null;

function updatePillDrop(e: MediaQueryList | MediaQueryListEvent) {
  pillDrop.value = e.matches ? "up" : "down";
}

onMounted(() => {
  mobileQuery = window.matchMedia("(max-width: 767px)");
  updatePillDrop(mobileQuery);
  mobileQuery.addEventListener("change", updatePillDrop);
});

onUnmounted(() => {
  mobileQuery?.removeEventListener("change", updatePillDrop);
});

// Schema.org ItemList pour carrousel Google Rich Results (slider hero)
// Déplacé depuis index.vue : désormais itère sur le tableau featured[] du slider.
// Utilise une fonction réactive pour que nuxt-schema-org mette à jour le JSON-LD
// quand les données lazy sont chargées.
useSchemaOrg([
  {
    "@type": "ItemList" as const,
    itemListElement: () =>
      featured.value
        .filter((e) => e.heroPhoto?.url && e.departmentCodeSlug)
        .map((e, i) => ({
          "@type": "ListItem" as const,
          position: i + 1,
          item: {
            "@type": "BedAndBreakfast" as const,
            name: e.name,
            address: buildListingPostalAddress(e),
            url: `${config.public.siteUrl}/${e.departmentCodeSlug}/${e.slug}`,
            image: e.heroPhoto.url,
            ...(e.googleRating && e.googleReviewCount
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating" as const,
                    ratingValue: e.googleRating,
                    bestRating: 5,
                    reviewCount: e.googleReviewCount,
                  },
                }
              : {}),
            // Format strict `€X` : Google ignore le texte libre dans `priceRange`.
            ...(e.priceFrom ? { priceRange: `€${e.priceFrom}` } : {}),
          },
        })),
  },
]);
</script>

<template>
  <section
    id="hero-v3"
    class="relative bg-paper-50 text-paper-900 -mt-14"
    style="padding-top: 3.5rem"
    :style="{ '--hero-underline-color': accentInkVar }"
  >
    <!-- Titre principal unique pour SEO + lecteurs d'écran. Il porte l'INTENTION
         DE RECHERCHE (`seoHeading`), pas l'accroche de marque : le h1 est le signal
         le plus lourd de la page et la Search Console montrait une home indexée sur
         la seule marque. L'accroche de marque (`titlePrefix` + `titleEmphasized`)
         est un paragraphe aria-hidden, pour n'avoir qu'un seul titre dans le DOM
         (audit Bing Webmaster Tools). Ne pas refusionner les clés : le visuel et
         le h1 disent volontairement deux choses différentes. -->
    <h1 class="sr-only">
      {{ $t("pages.home.heroV3.seoHeading") }}
    </h1>

    <div
      class="container mx-auto px-4 md:px-10 lg:px-6 xl:px-8 pt-6 md:pt-10 lg:pt-16 xl:pt-20 pb-10 md:pb-12 lg:pb-20 xl:pb-24"
    >
      <div
        class="grid gap-y-8 md:gap-y-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:grid-rows-[auto_1fr] gap-x-14"
      >
        <!-- A — eyebrow, accroche, parcours (homeWizard) ou sélecteur de type + pill -->
        <div class="@container min-w-0 flex flex-col gap-5 md:gap-6">
          <p class="tag text-sage-700">
            {{ eyebrowLabel }}
          </p>

          <!-- < 400 px : taille bornée à 10vw — « et personne d'autre. » est en
               `nowrap` et déborderait à 40 px sur un écran de 320 px.
               ≥ md : une seule ligne sur toute la largeur de la colonne. La phrase
               mesure ~15,7 em : 6,2 % de la largeur du conteneur (`cqi`) la fait
               tenir avec une marge, plafonnée à 60 px. -->
          <p
            class="font-display font-light tracking-[-0.025em] leading-[1.02] mb-2 md:mb-4 lg:mb-6 text-[clamp(30px,10vw,40px)] md:text-[min(60px,6.2cqi)] text-paper-900"
            aria-hidden="true"
          >
            {{ $t("pages.home.heroV3.titlePrefix") }}
            <span class="hero-underline font-light">{{
              $t("pages.home.heroV3.titleEmphasized")
            }}</span>
          </p>

          <HoliHomeSearchWizard
            v-if="config.public.homeWizard"
            :regions="regions"
            :total-count="typedCount"
          />
          <div v-else class="flex flex-col gap-5">
            <div>
              <HoliHomeTypeSwitcher />
            </div>
            <HoliHeroSearchPill :drop="pillDrop" />
          </div>

          <!-- Parcours : claims fixes sous le parcours, pleine largeur -->
          <HoliTrustStripV3 v-if="config.public.homeWizard" variant="fixed" />
        </div>

        <!-- B — « À la une » : sous la pill < lg, colonne droite ≥ lg.
             Padding haut ≥ lg : la photo descend au niveau du parcours, sous l'accroche.
             Padding droite/bas ≥ lg : débords de l'inclinaison, de
             l'index-card flottante et de la nav placée sous la photo. -->
        <div
          class="min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:pt-28 xl:pt-36 lg:pr-5 xl:pr-8 lg:pb-16"
        >
          <div
            v-if="isFeaturedLoading"
            class="w-full aspect-[16/9] lg:w-[260px] xl:w-[320px] 2xl:w-[380px] lg:aspect-[4/5] rounded-2xl bg-paper-100 animate-pulse"
          />
          <HoliFeaturedThisWeekV3 v-else :featured="featured" />
        </div>

        <!-- C — bandeau de confiance (sélecteur + pill seulement) : sous « À la une »
             < lg, sous la pill ≥ lg. Avec le parcours, claims fixes en zone A. -->
        <div
          v-if="!config.public.homeWizard"
          class="min-w-0 lg:col-start-1 lg:row-start-2 lg:self-start"
        >
          <HoliTrustStripV3 />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Soulignement animé pour l'emphasis du H1 hero (remplace l'ancien italic).
   Le trait se dessine de gauche à droite au mount, puis reste stable. Sa couleur
   suit le type actif du switcher via `--hero-underline-color`, posée sur la
   section racine (spec 4.74.1) ; le sage reste le repli si la variable manque. */
.hero-underline {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  padding-bottom: 0.18em;
}

.hero-underline::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -0.05em;
  height: 0.06em;
  background: var(--hero-underline-color, var(--color-sage-700, #3f5638));
  border-radius: 2px;
  transition: background-color 200ms ease;
  transform: scaleX(0);
  transform-origin: left center;
  animation: hero-underline-draw 900ms cubic-bezier(0.22, 0.61, 0.36, 1) 400ms
    forwards;
}

@keyframes hero-underline-draw {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-underline::after {
    animation: none;
    transform: scaleX(1);
    transition: none;
  }
}
</style>
